import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';

import AuthContext from '../../authAndConnections/auth';
import AxiosGlobal from '../../authAndConnections/axiosGlobalUrl';
import ConfirmDialog from '../../../tools/modal/confirmDialog';

// Pallet templates: a pallet of a list saved under a name (its rows and its processing type), kept per branch, so another pallet
// can start from it in any packing list. A pallet made from a template gets its own ID and its own pallet code: those belong to one
// physical pallet and a template holds neither. Anyone who may create or edit a packing list may keep and remove them.

// What the keyboard typed, in the digits a code and a count are stored in (۳۰ and ٣٠ are 30).
export const latinDigits = (v) => String(v == null ? '' : v)
  .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
  .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)));

// How many pallets one pick can add. A truck carries far fewer, and the form has to stay quick to fill in afterwards.
export const MAX_PALLETS_AT_ONCE = 100;
// Nothing usable (empty, 0) is one pallet; more than the limit is the limit.
const readCount = (text) => {
  const n = parseInt(text, 10);
  return Number.isFinite(n) && n >= 1 ? Math.min(n, MAX_PALLETS_AT_ONCE) : 1;
};

export function usePalletTemplates(branchId, active) {
  const authCtx = useContext(AuthContext);
  const axiosGlobal = useContext(AxiosGlobal);
  const api = `${axiosGlobal.defaultTargetApi}/mis/pallet-templates`;
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    if (!active || !branchId) return undefined;
    let cancelled = false;
    authCtx.jwtInst({ method: 'get', url: api, params: { branchId } })
      .then((res) => { if (!cancelled) setTemplates(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (!cancelled) setTemplates([]); });
    return () => { cancelled = true; };
  }, [active, branchId, authCtx, api]);

  const save = useCallback(async (template) => {
    const res = await authCtx.jwtInst({ method: 'post', url: api, data: { ...template, branchId } });
    setTemplates((list) => [...list, res.data].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true })));
    return res.data;
  }, [authCtx, api, branchId]);

  const remove = useCallback(async (id) => {
    await authCtx.jwtInst({ method: 'delete', url: `${api}/${id}` });
    setTemplates((list) => list.filter((row) => row._id !== id));
  }, [authCtx, api]);

  return { templates, save, remove };
}

// What a template holds, in a few words: the stone, how many sizes, how many pieces.
export function templateSummary(template, t) {
  const items = template.items || [];
  const codes = [...new Set(items.map((it) => String(it.code || '').trim()).filter(Boolean))];
  const pcs = items.reduce((sum, it) => sum + (Number(it.pcs) || 0), 0);
  return [
    codes.slice(0, 2).join(', ') + (codes.length > 2 ? ` +${codes.length - 2}` : ''),
    template.processingType,
    t('mis.plTemplateRows', { count: items.length }),
    pcs ? `${pcs} ${t('mis.plColPcs')}` : '',
  ].filter(Boolean).join(' · ');
}

// Saving a pallet as a template: it only asks for a name, and starts from one made of what the pallet holds.
export function SavePalletTemplateDialog({ open, pallet, onSave, onClose, T }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !pallet) return;
    const code = (pallet.items || []).map((it) => String(it.code || '').trim()).find(Boolean) || pallet.productCode || '';
    setName([code, pallet.processingType].filter(Boolean).join(' '));
    setBusy(false); setError('');
  }, [open, pallet]);

  const submit = async () => {
    if (!name.trim()) { setError(t('mis.plTemplateNameRequired')); return; }
    setBusy(true); setError('');
    try {
      await onSave(name.trim());
      onClose();
    } catch (err) {
      const code = err && err.response && err.response.data && err.response.data.code;
      setError(code === 'TEMPLATE_NAME_TAKEN' ? t('mis.plTemplateNameTaken', { name: name.trim() })
        : code === 'TEMPLATE_EMPTY' ? t('mis.plTemplateEmpty') : t('mis.plTemplateSaveFailed'));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs"
      sx={{ zIndex: (th) => th.zIndex.modal + 2 }}
      PaperProps={{ sx: { borderRadius: '14px' }, 'data-testid': 'pl-template-dialog' }}>
      <Box sx={{ p: 2.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: T.TEXT_PRI }}>{t('mis.plTemplateSaveTitle')}</Typography>
        <Typography sx={{ fontSize: '0.74rem', color: T.TEXT_SEC, mt: 0.5, mb: 2 }}>{t('mis.plTemplateSaveHelp')}</Typography>
        <TextField autoFocus fullWidth size="small" label={t('mis.plTemplateName')} value={name}
          InputLabelProps={{ shrink: true }} error={Boolean(error)} helperText={error || ' '}
          inputProps={{ maxLength: 80, 'data-testid': 'pl-template-name' }}
          onChange={(e) => { setName(e.target.value); setError(''); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 0.5 }}>
          <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={submit} disabled={busy} data-testid="pl-template-save"
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null} sx={{ textTransform: 'none', minWidth: 90 }}>
            {t('common.save')}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

// "Add from template": how many pallets, then the branch's templates. Picking one adds that many pallets made from it. Filled from the
// keyboard: it opens with the cursor in the count, Enter (or the down arrow) goes on to the templates, the arrows walk them and Enter
// picks. Every opening starts from 1, so a 30 from last time never adds 30 by surprise. The ✕ takes a template away for the whole
// branch, so it asks first; a pallet already made from it keeps its rows.
export function PalletTemplateMenu({ templates, canManage, onPick, onRemove, onError, touch, T }) {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [countText, setCountText] = useState('1');
  const countRef = useRef(null);
  const listRef = useRef(null);

  if (!templates.length) return null;
  const count = readCount(countText);
  const tooMany = Number(countText) > MAX_PALLETS_AT_ONCE;
  const open = (e) => { setCountText('1'); setAnchor(e.currentTarget); };
  const pick = (template) => { setAnchor(null); onPick(template, count); };
  const toTemplates = () => {
    const first = listRef.current && listRef.current.querySelector('[role="menuitem"]');
    if (first) first.focus();
  };
  const remove = async () => {
    const template = confirm;
    setConfirm(null);
    try { await onRemove(template._id); } catch (_) { onError(t('mis.plTemplateRemoveFailed')); }
  };

  return (
    <>
      <Button size="small" variant={touch ? 'outlined' : 'text'} data-testid="pl-template-add"
        startIcon={<BookmarkBorderIcon sx={{ fontSize: 14 }} />} onClick={open}
        sx={{ fontSize: '0.7rem', textTransform: 'none', '& .MuiButton-startIcon': { mx: 0, marginInlineEnd: 0.75 },
          ...(touch ? { width: '100%', minHeight: 48, fontSize: '0.85rem', borderStyle: 'dashed' } : {}) }}>
        {t('mis.plTemplateAdd')}
      </Button>
      {/* It opens low on the screen, so it has to clear the install / notification prompts (z-index 1600) as well as the form. */}
      <Popover anchorEl={anchor} open={Boolean(anchor) && !confirm} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }} transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        TransitionProps={{ onEntered: () => { if (countRef.current) { countRef.current.focus(); countRef.current.select(); } } }}
        sx={{ zIndex: (th) => th.zIndex.modal + 400 }}
        PaperProps={{ 'data-testid': 'pl-template-menu', sx: { display: 'flex', flexDirection: 'column', maxHeight: 420, width: touch ? 'calc(100vw - 32px)' : 380, maxWidth: '100%', borderRadius: '12px' } }}>
        <Box sx={{ p: 1.5, pb: 1, flexShrink: 0 }}>
          <TextField fullWidth size="small" label={t('mis.plTemplateCount')} value={countText} inputRef={countRef}
            InputLabelProps={{ shrink: true }} error={tooMany}
            helperText={t('mis.plTemplateCountHelp', { max: MAX_PALLETS_AT_ONCE })}
            onChange={(e) => setCountText(latinDigits(e.target.value).replace(/\D/g, '').slice(0, 3))}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'ArrowDown') { e.preventDefault(); toTemplates(); } }}
            inputProps={{ inputMode: 'numeric', dir: 'ltr', enterKeyHint: 'next', 'data-testid': 'pl-template-count',
              style: { fontWeight: 700, fontVariantNumeric: 'tabular-nums', ...(touch ? { fontSize: 16, minHeight: 28 } : {}) } }} />
        </Box>
        <MenuList ref={listRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pt: 0 }}>
        {templates.map((template) => (
          <MenuItem key={template._id} onClick={() => pick(template)} data-template-name={template.name}
            sx={{ gap: 1, alignItems: 'center', whiteSpace: 'normal', minHeight: touch ? 52 : 0 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: '0.82rem', fontWeight: 600, color: T.TEXT_PRI }}>{template.name}</Typography>
              <Typography noWrap sx={{ fontSize: '0.7rem', color: T.TEXT_SEC }}>{templateSummary(template, t)}</Typography>
            </Box>
            {count > 1 && (
              <Typography data-template-times sx={{ fontSize: '0.78rem', fontWeight: 700, color: T.TEXT_PRI, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                {`× ${count}`}
              </Typography>
            )}
            {canManage && (
              <Tooltip title={t('mis.plTemplateRemove')}>
                <IconButton size="small" aria-label={t('mis.plTemplateRemove')} data-template-remove={template.name}
                  sx={touch ? { width: 44, height: 44 } : undefined}
                  onClick={(e) => { e.stopPropagation(); setConfirm(template); }}>
                  <CloseIcon sx={{ fontSize: 15, color: T.TEXT_TER }} />
                </IconButton>
              </Tooltip>
            )}
          </MenuItem>
        ))}
        </MenuList>
      </Popover>
      <ConfirmDialog open={Boolean(confirm)} destructive title={t('mis.plTemplateRemoveTitle')}
        message={confirm ? t('mis.plTemplateRemoveBody', { name: confirm.name }) : ''}
        confirmLabel={t('common.delete')} cancelLabel={t('common.cancel')} onConfirm={remove} onClose={() => setConfirm(null)}
        sx={{ zIndex: (th) => th.zIndex.modal + 401 }} />
    </>
  );
}
